import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiDownload } from 'react-icons/hi';
import { Modal, Button, Input } from '@/components/common';
import { Customer } from '@/types/customer';
import { InvoiceResponse } from '@/types/invoice';

interface CustomerDebtExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer | null;
  invoices: InvoiceResponse[];
  onExport: (dateFrom: string, dateTo: string) => Promise<void>;
  initialDateFrom?: string;
  initialDateTo?: string;
}

/**
 * CustomerDebtExportModal provides date range selection for customer debt export
 */
const CustomerDebtExportModal: React.FC<CustomerDebtExportModalProps> = ({
  isOpen,
  onClose,
  customer,
  invoices,
  onExport,
  initialDateFrom,
  initialDateTo,
}) => {
  const { t } = useTranslation();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  // Set date range from parent's selected dates, or fallback to current month
  React.useEffect(() => {
    if (isOpen) {
      if (initialDateFrom) {
        // Convert ISO string to YYYY-MM-DD format for date input
        setDateFrom(initialDateFrom.split('T')[0]);
      } else if (!dateFrom) {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        setDateFrom(firstDay.toISOString().split('T')[0]);
      }

      if (initialDateTo) {
        setDateTo(initialDateTo.split('T')[0]);
      } else if (!dateTo) {
        const now = new Date();
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setDateTo(lastDay.toISOString().split('T')[0]);
      }
    }
  }, [isOpen, initialDateFrom, initialDateTo]);

  const handleExport = async () => {
    if (!dateFrom || !dateTo) {
      return;
    }

    setLoading(true);
    try {
      await onExport(dateFrom, dateTo);
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDateFrom('');
    setDateTo('');
    onClose();
  };

  const isValidDateRange = dateFrom && dateTo && new Date(dateFrom) <= new Date(dateTo);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('customers.debtExport.title')}
      size="md"
      footer={
        <div className="flex items-center justify-end space-x-3">
          <Button
            variant="outline"
            size="md"
            onClick={handleClose}
            disabled={loading}
          >
            {t('buttons.cancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleExport}
            disabled={!isValidDateRange || loading}
            loading={loading}
            icon={<HiDownload className="h-4 w-4" />}
          >
            {t('customers.debtExport.export')}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Customer Information */}
        {customer && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              {t('customers.debtExport.customerInfo')}
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">{t('customers.fields.name')}:</span>
                <span className="ml-2 font-medium">{customer.name}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('customers.fields.code')}:</span>
                <span className="ml-2 font-medium">{customer.code}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('customers.fields.phone')}:</span>
                <span className="ml-2">{customer.phone || '-'}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('customers.fields.totalDebt')}:</span>
                <span className="ml-2 font-medium text-red-600">
                  {new Intl.NumberFormat('vi-VN').format(customer.totalDebt || 0)} VND
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Date Range Selection */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            {t('customers.debtExport.dateRange')}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('customers.debtExport.dateFrom')}
              </label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('customers.debtExport.dateTo')}
              </label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                disabled={loading}
                required
              />
            </div>
          </div>
          {dateFrom && dateTo && new Date(dateFrom) > new Date(dateTo) && (
            <p className="mt-2 text-sm text-red-600">
              {t('customers.debtExport.invalidDateRange')}
            </p>
          )}
        </div>

        {/* Export Preview */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">
            {t('customers.debtExport.exportPreview')}
          </h3>
          <p className="text-sm text-blue-800 mb-3">
            {t('customers.debtExport.exportDescription')}
          </p>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• {t('customers.debtExport.includeHeaders')}</li>
            <li>• {t('customers.debtExport.includeCustomerInfo')}</li>
            <li>• {t('customers.debtExport.includeInvoiceDetails')}</li>
            <li>• {t('customers.debtExport.includeLineItems')}</li>
            <li>• {t('customers.debtExport.includeSignature')}</li>
          </ul>
          {invoices.length > 0 && (
            <p className="text-sm text-blue-600 mt-3">
              {invoices.length} {invoices.length === 1 ? 'invoice' : 'invoices'} available for export
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CustomerDebtExportModal;
